import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { catchError, filter, map, of, startWith, switchMap, take } from 'rxjs';
import { CurrentUserService } from '../../../core/auth/current-user.service';
import { BusinessService } from '../../../core/services/business.service';
import { SubscriptionPlansService } from '../../../core/services/subscription-plans.service';
import { AuthService } from '../../../core/auth/auth.service';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  permission?: string;
  anyPermission?: string[];
  requiresCustomRoles?: boolean;
}

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar implements OnInit {
  private readonly router = inject(Router);
  private readonly currentUser = inject(CurrentUserService);
  private readonly businesses = inject(BusinessService);
  private readonly plans = inject(SubscriptionPlansService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly auth = inject(AuthService);
  businessSetupState: 'loading' | 'profile' | 'review' | 'plan' | 'active' = 'loading';
  allowCustomRoles = false;
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly adminNavItems: NavItem[] = [
    { label: 'Dashboard', route: '/admin/dashboard', icon: 'grid_view', permission: 'PLATFORM_DASHBOARD_VIEW' },
    { label: 'Businesses & Access', route: '/admin/businesses', icon: 'domain', permission: 'PLATFORM_BUSINESS_VIEW' },
    { label: 'Subscription Plans', route: '/admin/subscription-plans', icon: 'workspace_premium', permission: 'SUBSCRIPTION_PLAN_VIEW' },
    { label: 'Admin Users', route: '/admin/users', icon: 'group', permission: 'USER_VIEW' },
    { label: 'Business Roles', route: '/admin/roles', icon: 'badge', permission: 'ROLE_VIEW' },
    { label: 'Role Permissions', route: '/admin/permissions', icon: 'shield_lock', permission: 'PERMISSION_VIEW' },
    { label: 'Listing approvals', route: '/admin/listing-approvals', icon: 'fact_check', permission: 'LISTING_REVIEW_VIEW' },
    { label: 'Catalog', route: '/admin/catalog', icon: 'category', anyPermission: ['CATEGORY_VIEW', 'BRAND_VIEW', 'PROFIT_RULE_VIEW'] },
    { label: 'Business Locations', route: '/admin/locations', icon: 'location_on', permission: 'LOCATION_VIEW' },
    { label: 'Finance & Budgets', route: '/admin/finance', icon: 'account_balance_wallet', permission: 'FINANCE_OVERVIEW_VIEW' },
    { label: 'Reports', route: '/admin/reports', icon: 'monitoring', permission: 'REPORT_VIEW' },
    { label: 'App Content', route: '/admin/app-content', icon: 'policy', anyPermission: ['CONTENT_VIEW', 'POLICY_VIEW'] },
    { label: 'Support Tickets', route: '/admin/support-tickets', icon: 'support_agent', permission: 'SUPPORT_TICKET_VIEW' },
    { label: 'FAQs', route: '/admin/faqs', icon: 'quiz', permission: 'FAQ_VIEW' },
  ];

  readonly businessNavItems: NavItem[] = [
    { label: 'Dashboard', route: '/business/dashboard', icon: 'grid_view', permission: 'DASHBOARD_VIEW' },
    {
      label: 'Record Purchase',
      route: '/business/purchases',
      icon: 'shopping_cart_checkout',
      anyPermission: ['PROCUREMENT_COMPANY_USE', 'PROCUREMENT_NEW_PRODUCT', 'PROCUREMENT_USED_PRODUCT'],
    },
    { label: 'Purchase List', route: '/business/purchase-list', icon: 'receipt_long', permission: 'PURCHASE_VIEW' },
    { label: 'Product Listings', route: '/business/products', icon: 'inventory_2', permission: 'PRODUCT_VIEW' },
    {
      label: 'Sell',
      route: '/business/sales',
      icon: 'point_of_sale',
      anyPermission: ['SALE_VIEW', 'SALE_CREATE'],
    },
    { label: 'Internal Sales', route: '/business/internal-sales', icon: 'receipt', permission: 'SALE_VIEW' },
    { label: 'Business Locations', route: '/business/locations', icon: 'location_on', permission: 'LOCATION_VIEW' },
    {
      label: 'Inventory',
      route: '/business/inventory',
      icon: 'warehouse',
      permission: 'INVENTORY_VIEW',
    },
    { label: 'Finance & Budgets', route: '/business/finance', icon: 'account_balance_wallet', permission: 'FINANCE_OVERVIEW_VIEW' },
    { label: 'Profit Rules', route: '/business/profit-rules', icon: 'percent', permission: 'PROFIT_RULE_VIEW' },
    { label: 'Orders', route: '/business/orders', icon: 'receipt_long', permission: 'ORDER_VIEW' },
    { label: 'Reports', route: '/business/reports', icon: 'monitoring', permission: 'REPORT_VIEW' },
    { label: 'Users', route: '/business/users', icon: 'group', permission: 'USER_VIEW' },
    {
      label: 'Roles',
      route: '/business/roles',
      icon: 'admin_panel_settings',
      permission: 'ROLE_VIEW',
      requiresCustomRoles: true,
    },
    {
      label: 'Permissions',
      route: '/business/permissions',
      icon: 'lock',
      permission: 'PERMISSION_VIEW',
      requiresCustomRoles: true,
    },
  ];

  ngOnInit(): void {
    if (!this.isBusinessPortal) return;
    if (!this.currentUser.isBusinessOwner()) {
      this.businessSetupState = 'active';
      return;
    }
    this.auth.ensureFreshSession(0).pipe(
      switchMap(() => this.businesses.myApprovalStatus()),
      switchMap((status) => {
        if (status.businessKind === 'PlatformOwned') {
          this.allowCustomRoles = true;
          return of<'active'>('active');
        }
        if (status.approvalStatus !== 'Approved') return of<'review'>('review');
        return this.businesses.mine().pipe(
          switchMap((business) => {
            if (!business) return of<'profile'>('profile');
            return this.plans.mine().pipe(map((subscription) => {
              this.allowCustomRoles = Boolean(subscription?.plan?.allowCustomRoles);
              return subscription?.status === 'Active' ? 'active' as const : 'plan' as const;
            }));
          }),
        );
      }),
      take(1),
      catchError(() => of<'profile'>('profile')),
    ).subscribe((state) => {
      this.businessSetupState = state;
      this.cdr.detectChanges();
    });
  }

  get isBusinessPortal(): boolean {
    return this.currentUrl().startsWith('/business');
  }

  get navItems(): NavItem[] {
    if (this.isBusinessPortal && this.businessSetupState !== 'active') {
      if (this.businessSetupState === 'plan') {
        return [{ label: 'Choose subscription plan', route: '/business/plan-selection', icon: 'workspace_premium' }];
      }
      return [{
        label: this.businessSetupState === 'review' ? 'Application status' : 'Complete application',
        route: '/business/onboarding',
        icon: this.businessSetupState === 'review' ? 'fact_check' : 'assignment_add',
      }];
    }
    const items = this.isBusinessPortal ? this.businessNavItems : this.adminNavItems;
    const isBusinessOwner = this.isBusinessPortal && this.currentUser.isBusinessOwner();
    const isSuperAdmin = !this.isBusinessPortal && (this.currentUser.user()?.roles ?? [this.currentUser.user()?.role ?? ''])
      .some((role) => role === 'SuperAdmin' || role === 'PlatformSuperAdmin');
    if (isSuperAdmin) return items;
    return items.filter(
      (item) =>
        ((isBusinessOwner && item.route === '/business/purchase-list') ||
        ((!item.permission || this.currentUser.hasPermission(item.permission)) &&
          (!item.anyPermission ||
            item.anyPermission.some((permission) => this.currentUser.hasPermission(permission))) &&
          (!item.requiresCustomRoles || this.allowCustomRoles))),
    );
  }

  get homeRoute(): string {
    if (!this.isBusinessPortal) return '/admin/dashboard';
    if (this.businessSetupState === 'active') return this.currentUser.businessLandingRoute();
    return this.businessSetupState === 'plan' ? '/business/plan-selection' : '/business/onboarding';
  }

  get portalTitle(): string {
    return this.isBusinessPortal ? 'Business Portal' : 'Platform Admin';
  }

  get workspaceLabel(): string {
    if (!this.isBusinessPortal) return 'Platform governance';
    if (this.businessSetupState === 'profile') return 'Complete your application';
    if (this.businessSetupState === 'review') return 'Approval pending';
    if (this.businessSetupState === 'plan') return 'Plan required';
    return 'Seller workspace';
  }

  get workspaceName(): string {
    if (!this.isBusinessPortal || this.businessSetupState === 'active') return 'ZepBazaar';
    return 'Business setup';
  }
}
